import type { WebhookEventTypesResponse } from '@cobliteam/api-lib/src/dashboard/webhooks';
import { WebhooksRequester } from '@cobliteam/api-lib/src/dashboard/webhooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import useWebhookEventTypes from './useWebhookEventTypes';

const webhooksRequester = new WebhooksRequester();

const EVENT_TYPES_QUERY_KEY = 'webhook-event-types';

/**
 * Returns allowed event types split into telemetry and camera groups.
 * - Group definitions come from GET /v1/signatures/event-types (iris).
 * - Product-type filtering (CAM_PRO, TELEMETRY, etc.) is handled by useWebhookEventTypes.
 * - Falls back to the last successful grouping if the endpoint is temporarily unavailable.
 */
const useWebhookEventTypesGrouped = () => {
  const { data: eventTypesFromBackend } = useQuery({
    queryKey: [EVENT_TYPES_QUERY_KEY],
    queryFn: webhooksRequester.getEventTypes,
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const lastValidGrouping = useRef<WebhookEventTypesResponse | null>(null);

  useEffect(() => {
    if (eventTypesFromBackend) {
      lastValidGrouping.current = eventTypesFromBackend;
    }
  }, [eventTypesFromBackend]);

  const allowedEventTypes = useWebhookEventTypes();

  return useMemo(() => {
    const grouping = eventTypesFromBackend ?? lastValidGrouping.current;
    const cameraSet = new Set(grouping?.camera ?? []);
    return {
      telemetry: allowedEventTypes.filter((t) => !cameraSet.has(t)),
      camera: allowedEventTypes.filter((t) => cameraSet.has(t)),
    };
  }, [allowedEventTypes, eventTypesFromBackend]);
};

export default useWebhookEventTypesGrouped;
